import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { StepRail } from './canvas/StepRail';
import { StepCard } from './canvas/StepCard';
import type { StepState, WorkflowPhase } from '@/lib/workflowStreamProtocol';

interface WorkflowCanvasProps {
  steps: StepState[];
  activeStepId: string | null;
  workflowPhase: WorkflowPhase;
  onSelectStep?: (stepId: string) => void;
}

const PHASE_PILL: Record<WorkflowPhase, { label: string; className: string }> = {
  discovery: { label: 'proposed · awaiting answers', className: 'bg-warning/15 text-warning-foreground border-warning/30' },
  executing: { label: 'in progress', className: 'bg-primary/15 text-primary border-primary/30' },
  finished: { label: 'finished', className: 'bg-success/15 text-success-foreground border-success/30' },
};

export function WorkflowCanvas({ steps, activeStepId, workflowPhase, onSelectStep }: WorkflowCanvasProps) {
  const phasePill = PHASE_PILL[workflowPhase];

  return (
    <aside className="flex flex-col bg-card/30 border-l border-border min-h-0 min-w-0 overflow-hidden">
      <header className="h-[52px] border-b border-border flex items-center justify-between px-4 shrink-0">
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Canvas · archive</div>
        <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${phasePill.className}`}>
          {phasePill.label}
        </Badge>
      </header>

      <div className="px-3 py-2 border-b border-border shrink-0">
        <StepRail steps={steps} activeStepId={activeStepId} workflowPhase={workflowPhase} onSelectStep={onSelectStep} />
        {steps.length > 0 && (
          <div className="text-[10px] text-muted-foreground text-center mt-1">
            click any step to focus
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0 min-w-0">
        <div className="p-3 space-y-2 min-w-0">
          {steps.length === 0 ? (
            <div className="text-xs text-muted-foreground py-8 text-center px-4">
              Nothing archived yet. As Cosmo proposes your workflow, each step appears here with its prompt, output, analysis, and decisions.
            </div>
          ) : activeStepId ? (
            // Canvas mirrors the center stream: only the active step's card is
            // visible at a time. Use the horizontal rail above to jump between
            // steps; clicking a rail node updates activeStepId and the card
            // re-mounts (state resets because key=step.id).
            steps
              .filter(s => s.id === activeStepId)
              .map((step) => {
                const trueIndex = steps.findIndex(s => s.id === step.id);
                return (
                  <StepCard
                    key={step.id}
                    step={step}
                    index={trueIndex}
                    isActive
                    onFocus={onSelectStep}
                  />
                );
              })
          ) : (
            // No active step yet (early in discovery before the plan seeds) —
            // show all steps so the user sees the full proposal at a glance.
            steps.map((step, idx) => (
              <StepCard
                key={step.id}
                step={step}
                index={idx}
                isActive={false}
                onFocus={onSelectStep}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </aside>
  );
}
